import React, { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase_client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

type PageState = 'checking' | 'valid' | 'expired';

type InvitationInfo = {
  invitation_id: string;
  email: string;
  org_id: string;
  org_name?: string;
  org_address?: string;
  country_iso?: string;
  role_id?: number;
  status?: string;
};

function parseAuthRedirectError(): { code?: string; description?: string } | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const error = search.get('error') || hash.get('error');
  const errorCode = search.get('error_code') || hash.get('error_code');
  const description = search.get('error_description') || hash.get('error_description');
  if (!error && !errorCode) return null;
  return {
    code: errorCode || error || undefined,
    description: description
      ? decodeURIComponent(description.replace(/\+/g, ' '))
      : undefined,
  };
}

function isDeadInviteLink(code?: string, description?: string): boolean {
  const hay = `${code || ''} ${description || ''}`.toLowerCase();
  return (
    hay.includes('otp_expired') ||
    hay.includes('expired') ||
    hay.includes('access_denied') ||
    hay.includes('invalid')
  );
}

export default function CompleteProfilePage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [pageState, setPageState] = useState<PageState>('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [country, setCountry] = useState('MY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countries, setCountries] = useState<any[]>([]);
  const [countryPhoneCode, setCountryPhoneCode] = useState('+60');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [invitation, setInvitation] = useState<InvitationInfo | null>(null);
  const inviteResolvedRef = useRef(false);
  const authSubRef = useRef<{ unsubscribe: () => void } | null>(null);

  const invitationIdFromUrl =
    searchParams.get('invitation') || searchParams.get('token') || '';

  const applyInvitation = useCallback((info: InvitationInfo, lockedEmail?: string) => {
    setInvitation(info);
    setEmail(lockedEmail || info.email || '');
    setOrganizationName(info.org_name || '');
    setAddress(info.org_address || '');
    setCountry(info.country_iso || 'MY');
  }, []);

  const loadInvitationById = useCallback(async (invitationId: string) => {
    const { data, error } = await supabase.rpc('get_pending_invitation', {
      p_invitation_id: invitationId,
    });
    if (error) throw error;
    if (!data?.success) throw new Error(data?.error || 'Invitation not found');
    return data as InvitationInfo & { success: boolean };
  }, []);

  useEffect(() => {
    supabase
      .from('countries')
      .select('iso, name, country_phone_code')
      .order('name', { ascending: true })
      .then(({ data }) => setCountries(data || []));
  }, []);

  useEffect(() => {
    if (!country || countries.length === 0) return;
    const selectedCountry = countries.find((c) => c.iso === country);
    if (selectedCountry?.country_phone_code) {
      setCountryPhoneCode(selectedCountry.country_phone_code);
    }
  }, [country, countries]);

  useEffect(() => {
    let cancelled = false;
    let gotSession = false;

    const urlError = parseAuthRedirectError();
    const finishExpired = (message: string, info?: InvitationInfo | null) => {
      if (cancelled) return;
      if (info) applyInvitation(info);
      setMsg(message);
      setPageState('expired');
    };

    const finishValid = (info: InvitationInfo, sessionEmail: string, metadataName?: string) => {
      if (cancelled) return;
      inviteResolvedRef.current = true;
      applyInvitation(info, sessionEmail);
      if (metadataName) setDisplayName(metadataName);
      setMsg(null);
      setPageState('valid');
    };

    const resolveInvite = async (sessionUser: { id: string; email?: string; user_metadata?: any }) => {
      const metadata = sessionUser.user_metadata || {};
      const invitationId = invitationIdFromUrl || metadata.invitation_id;
      let info: (InvitationInfo & { success?: boolean }) | null = null;

      if (invitationId) {
        info = await loadInvitationById(invitationId);
      } else {
        const { data, error } = await supabase.rpc('get_my_pending_invitation');
        if (error) throw error;
        if (data?.success) info = data as InvitationInfo & { success: boolean };
      }

      if (!info) {
        finishExpired('This invite is invalid or is no longer pending. Ask your admin to send a new invitation.');
        return;
      }
      if (info.status && info.status !== 'pending') {
        finishExpired('This invitation has already been used. Please sign in.', info);
        return;
      }
      if (sessionUser.email && info.email && sessionUser.email.toLowerCase() !== info.email.toLowerCase()) {
        finishExpired('This invite email does not match the signed-in account. Ask your admin to resend the invitation.', info);
        return;
      }

      finishValid(info, sessionUser.email || info.email, metadata.display_name);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (
        inviteResolvedRef.current &&
        (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED')
      ) {
        return;
      }
      if (
        session?.user &&
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY' || event === 'USER_UPDATED')
      ) {
        gotSession = true;
        // Do not await auth work inside this callback — supabase-js holds a lock
        // here, and updateUser({ password }) will hang forever if it waits on it.
        window.setTimeout(() => {
          if (cancelled) return;
          resolveInvite(session.user).catch((err: any) => {
            finishExpired(err?.message || 'This invitation could not be loaded.');
          });
        }, 0);
      }
    });
    authSubRef.current = subscription;

    const timeoutId = window.setTimeout(async () => {
      if (cancelled || gotSession) return;

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        gotSession = true;
        try {
          await resolveInvite(session.user);
        } catch (err: any) {
          finishExpired(err?.message || 'This invitation could not be loaded.');
        }
        return;
      }

      let info: InvitationInfo | null = null;
      if (invitationIdFromUrl) {
        try {
          info = await loadInvitationById(invitationIdFromUrl);
        } catch {
          info = null;
        }
      }

      if (urlError && isDeadInviteLink(urlError.code, urlError.description)) {
        finishExpired(
          'This invitation link has expired or was already used. Ask your admin to resend the invitation.',
          info
        );
        return;
      }

      finishExpired(
        'This invitation link is invalid or has expired. Ask your admin to resend the invitation. Do not sign up as a new user.',
        info
      );
    }, 2500);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      authSubRef.current = null;
      subscription.unsubscribe();
    };
  }, [applyInvitation, invitationIdFromUrl, loadInvitationById]);

  const handleCompleteProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !invitation?.invitation_id) {
        setPageState('expired');
        setMsg('Your invite session expired. Ask your admin to resend the invitation.');
        return;
      }

      if (!password || password.length < 6) {
        throw new Error('Password is required (minimum 6 characters)');
      }

      authSubRef.current?.unsubscribe();
      authSubRef.current = null;

      let profileCountry = invitation.country_iso || country;
      let profilePhoneCode = countryPhoneCode;
      const selectedCountry = countries.find((c) => c.iso === profileCountry);
      if (selectedCountry) profilePhoneCode = selectedCountry.country_phone_code;

      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'finalize-invited-profile',
        {
          body: {
            invitation_id: invitation.invitation_id,
            password,
            display_name: displayName,
            contact_number: `${profilePhoneCode}${phoneNumber}`,
            country: profileCountry,
            locale: 'en',
          },
        }
      );

      if (fnError) throw new Error(fnError.message || 'Failed to complete profile');
      if (!fnData?.success) {
        throw new Error(fnData?.error || 'Failed to complete profile');
      }

      setMsg('Profile completed! Please sign in with your new password. Redirecting...');
      await signOut();
    } catch (err: any) {
      const message = err?.message ?? 'An error occurred. Please try again.';
      if (/auth session missing|not authenticated/i.test(message)) {
        setPageState('expired');
        setMsg('Your invite session expired. Ask your admin to resend the invitation.');
      } else {
        setMsg(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white shadow-lg rounded-xl w-full max-w-4xl p-8">
        <h2 className="text-2xl font-semibold text-center text-gray-800 mb-3">
          {pageState === 'expired' ? 'Invitation expired' : 'Complete Your Profile'}
        </h2>
        <p className="text-center text-gray-500 mb-6">
          {pageState === 'valid'
            ? 'You have an invitation! Complete your profile to join the team.'
            : pageState === 'expired'
              ? 'This page cannot create a new account. Use the latest invitation email from your admin.'
              : 'Verifying invitation...'}
        </p>

        {msg && (
          <div
            className={`text-center mb-4 p-2 rounded ${
              msg.toLowerCase().includes('success') ||
              msg.toLowerCase().includes('completed') ||
              msg.toLowerCase().includes('redirecting')
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {msg}
          </div>
        )}

        {pageState === 'checking' && (
          <p className="text-center text-gray-500">Please wait while we verify your invitation link.</p>
        )}

        {pageState === 'expired' && (
          <div className="text-center space-y-4">
            {email && (
              <p className="text-gray-600">
                Invited email: <strong>{email}</strong>
              </p>
            )}
            {organizationName && (
              <p className="text-gray-600">
                Organization: <strong>{organizationName}</strong>
              </p>
            )}
            <button
              type="button"
              onClick={() => navigate('/signin')}
              className="w-full max-w-sm bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors"
            >
              Go to sign in
            </button>
          </div>
        )}

        {pageState === 'valid' && (
          <form onSubmit={handleCompleteProfile} className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Organization Name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                    value={organizationName}
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Address</label>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                    value={address}
                    rows={5}
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Country</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                    value={country}
                    required
                    disabled
                  >
                    {countries.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Email</label>
                  <input
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 cursor-not-allowed"
                    value={email}
                    required
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Display Name</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="e.g., John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Phone Number</label>
                  <div className="flex">
                    <div className="relative">
                      <select
                        value={countryPhoneCode}
                        onChange={(e) => setCountryPhoneCode(e.target.value)}
                        className="appearance-none border border-gray-300 rounded-l-lg bg-white text-gray-700 pl-4 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none w-24 cursor-pointer"
                      >
                        {countries.map((c) => (
                          <option key={c.iso} value={c.country_phone_code}>
                            {c.country_phone_code}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="tel"
                      className="flex-1 border border-l-0 border-gray-300 rounded-r-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="123456789"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 mb-1 font-medium">Password</label>
                  <input
                    type="password"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? 'Completing Profile...' : 'Complete Profile & Join Team'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
